import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, User, Save, Plus } from 'lucide-react';
import AdminLayout from '../../components/admin/AdminLayout';
import { usersAPI } from '../../services/api';
import '../../styles/admin-shared.css';

const ADDICTION_TYPES = [
  { value: 'PORNOGRAPHY', label: 'Pornografia' },
  { value: 'ALCOHOL', label: 'Álcool' },
  { value: 'DRUGS', label: 'Drogas' },
  { value: 'GAMBLING', label: 'Apostas' },
  { value: 'SOCIAL_MEDIA', label: 'Redes Sociais' },
  { value: 'GAMING', label: 'Jogos' },
  { value: 'FOOD', label: 'Comida' },
  { value: 'SMOKING', label: 'Cigarro' },
  { value: 'SHOPPING', label: 'Compras' },
  { value: 'OTHER', label: 'Outro' },
];

const UserEditPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isNew = !id || id === 'new';
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    role: 'USER',
    addiction_type: '',
    is_active: true,
  });

  useEffect(() => {
    if (!isNew) fetchUser();
  }, [id]);

  const fetchUser = async () => {
    try {
      const res = await usersAPI.getById(id);
      const u = res.data.data;
      setFormData({
        name: u.name || '',
        email: u.email || '',
        password: '',
        role: u.role || 'USER',
        addiction_type: u.addiction_type || '',
        is_active: u.is_active ?? true,
      });
    } catch (error) {
      alert('Erro ao carregar usuário');
      navigate('/admin/users');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const data = { ...formData };
      if (!data.password) delete data.password;
      if (isNew) {
        await usersAPI.create(data);
        navigate('/admin/users');
      } else {
        await usersAPI.update(id, data);
        navigate(`/admin/users/${id}`);
      }
    } catch (error) {
      alert(error.response?.data?.message || 'Erro ao salvar usuário');
    } finally {
      setSaving(false);
    }
  };

  const backPath = isNew ? '/admin/users' : `/admin/users/${id}`;

  if (loading) {
    return (
      <AdminLayout>
        <div className="loading-container"><div className="spinner" /></div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div style={{ maxWidth: '640px' }}>
        <div className="page-header">
          <div className="user-detail-heading">
            <button className="btn btn-outline btn-sm" onClick={() => navigate(backPath)}>
              <ArrowLeft size={16} />
            </button>
            <div>
              <h1><User size={22} /> {isNew ? 'Novo Usuário' : 'Editar Usuário'}</h1>
              <p>{isNew ? 'Crie um novo usuário na plataforma' : 'Atualize as informações do usuário'}</p>
            </div>
          </div>
        </div>

        <div className="section-card">
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label>Nome *</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Nome completo"
                required
              />
            </div>

            <div className="form-group">
              <label>Email *</label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="email@exemplo.com"
                required
              />
            </div>

            <div className="form-group">
              <label>{isNew ? 'Senha *' : 'Nova Senha'}</label>
              <input
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                placeholder={isNew ? 'Mínimo 6 caracteres' : 'Deixe em branco para manter a atual'}
                required={isNew}
                minLength={6}
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Função *</label>
                <select
                  value={formData.role}
                  onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                  required
                >
                  <option value="USER">Usuário</option>
                  <option value="MODERATOR">Moderador</option>
                  <option value="ADMIN">Admin</option>
                </select>
              </div>

              <div className="form-group">
                <label>Tipo de Vício</label>
                <select
                  value={formData.addiction_type}
                  onChange={(e) => setFormData({ ...formData, addiction_type: e.target.value })}
                >
                  <option value="">Não informado</option>
                  {ADDICTION_TYPES.map(t => (
                    <option key={t.value} value={t.value}>{t.label}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="form-group checkbox-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={formData.is_active}
                  onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                />
                <span>Usuário ativo (pode fazer login)</span>
              </label>
            </div>

            <div className="form-actions">
              <button type="button" className="btn btn-outline" onClick={() => navigate(backPath)}>
                Cancelar
              </button>
              <button type="submit" className="btn btn-primary" disabled={saving}>
                {saving
                  ? 'Salvando...'
                  : isNew
                    ? <><Plus size={16} /> Criar Usuário</>
                    : <><Save size={16} /> Salvar Alterações</>}
              </button>
            </div>
          </form>
        </div>
      </div>
    </AdminLayout>
  );
};

export default UserEditPage;
